import axiosInstance from "../auth/axios";

/**
 * Retrieves one bounded page of Financial Seasons for a Site.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {Object} [options] - Offset pagination, focus, and cancellation options.
 * @param {number} [options.page=1] - One-based page number.
 * @param {number} [options.limit=25] - Rows per page.
 * @param {number|string|null} [options.focusSeasonId=null] - Season whose containing page should be returned.
 * @param {boolean} [options.preferActive=false] - Whether to return the page containing the active Season.
 * @param {AbortSignal} [options.signal] - Optional request cancellation signal.
 * @returns {Promise<Object>} Bounded Season page with active-season and pagination metadata.
 */
export const getFinancialSeasons = async (
  farmId,
  { page = 1, limit = 25, focusSeasonId = null, preferActive = false, signal } = {}
) => {
  const response = await axiosInstance.get(`/financial/${farmId}/seasons`, {
    signal,
    params: {
      page,
      limit,
      ...(focusSeasonId ? { focusSeasonId } : {}),
      ...(preferActive ? { preferActive: true } : {}),
    },
  });
  return response.data;
};

/**
 * Retrieves the currently active Financial season for a Site.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {AbortSignal} [signal] - Optional request cancellation signal.
 * @returns {Promise<Object|null>} The active Financial season, or null.
 */
export const getActiveFinancialSeason = async (farmId, signal = undefined) => {
  const response = await axiosInstance.get(`/financial/${farmId}/seasons/active`, { signal });
  return response.data;
};

/**
 * Starts a new Financial season for a Site.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {string} startDate - Season start date in YYYY-MM-DD format.
 * @param {string|null} [endDate=null] - End date for a completed historical Season.
 * @returns {Promise<Object>} The created Financial season.
 */
export const startFinancialSeason = async (farmId, startDate, endDate = null) => {
  const response = await axiosInstance.post(`/financial/${farmId}/seasons`, {
    start_date: startDate,
    ...(endDate ? { end_date: endDate } : {}),
  });

  return response.data;
};

/**
 * Ends an active Financial season.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {string} endDate - Season end date in YYYY-MM-DD format.
 * @param {Object} [options={}] - Optional later-OPEX/Sales resolution and concurrency snapshots.
 * @returns {Promise<Object>} The completed Financial lifecycle result.
 */
export const endFinancialSeason = async (farmId, seasonId, endDate, options = {}) => {
  const response = await axiosInstance.patch(`/financial/${farmId}/seasons/${seasonId}/end`, {
    end_date: endDate,
    ...(options.resolution
      ? {
          resolution: options.resolution,
          affected_periods: options.affectedPeriods || [],
        }
      : {}),
    ...(options.nextStartDate
      ? {
          next_start_date: options.nextStartDate,
        }
      : {}),
    ...(options.affectedSales
      ? {
          affected_sales: options.affectedSales,
        }
      : {}),
    ...(options.affectedDailyExpenses
      ? { affected_daily_expenses: options.affectedDailyExpenses }
      : {}),
  });

  return response.data;
};

/**
 * Deletes a Financial season.
 *
 * Populated seasons require explicit confirmation before their
 * OPEX and Sales data can be permanently removed.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {boolean} confirmDataDelete - Whether destructive deletion was explicitly confirmed.
 * @param {string|null} deleteToken - Snapshot token returned by the confirmation-required response.
 * @returns {Promise<Object>} The deletion result.
 */
export const deleteFinancialSeason = async (farmId, seasonId, confirmDataDelete = false, deleteToken = null) => {
  const response = await axiosInstance.delete(`/financial/${farmId}/seasons/${seasonId}`, {
    params: {
      confirmDataDelete,
      ...(deleteToken ? { deleteToken } : {}),
    },
  });

  return response.data;
};

/**
 * Retrieves the aggregated Seasonal P&L report for one Financial season.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {AbortSignal} [signal] - Optional request cancellation signal.
 * @returns {Promise<Object>} The selected season's read-only Seasonal P&L report.
 */
export const getFinancialHistory = async (farmId, seasonId, signal = undefined) => {
  const response = await axiosInstance.get(`/financial/${farmId}/seasons/${seasonId}/history`, { signal });
  return response.data;
};

/**
 * Retrieves an OPEX log for one month in a Financial season.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {number} year - OPEX year.
 * @param {number} month - OPEX month.
 * @param {AbortSignal} [signal] - Optional request cancellation signal.
 * @returns {Promise<Object>} The requested OPEX log.
 */
export const getOpexLog = async (farmId, seasonId, year, month, signal = undefined) => {
  const response = await axiosInstance.get(`/financial/${farmId}/seasons/${seasonId}/opex/${year}/${month}`, {
    signal,
  });

  return response.data;
};

/**
 * Creates an OPEX log for one month in a Financial season.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {number} year - OPEX year.
 * @param {number} month - OPEX month.
 * @returns {Promise<Object>} The created OPEX log.
 */
export const createOpexLog = async (farmId, seasonId, year, month) => {
  const response = await axiosInstance.post(`/financial/${farmId}/seasons/${seasonId}/opex`, {
    year,
    month,
  });

  return response.data;
};

/**
 * Retrieves a compact server-side revision token for one complete OPEX month.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {number|string} seasonId - The Financial season ID.
 * @param {number} year - OPEX year.
 * @param {number} month - OPEX month.
 * @returns {Promise<{revisionToken: string}>} Fixed-size OPEX deletion snapshot token.
 */
export const getOpexDeleteToken = async (farmId, seasonId, year, month) => {
  const response = await axiosInstance.get(
    `/financial/${farmId}/seasons/${seasonId}/opex/${year}/${month}/delete-token`
  );
  return response.data;
};

/** Deletes one complete OPEX month if its server-side revision token is still current. */
export const deleteOpexLog = async (farmId, seasonId, year, month, expectedRevisionToken) => {
  const response = await axiosInstance.delete(`/financial/${farmId}/seasons/${seasonId}/opex/${year}/${month}`, {
    data: {
      expected_revision_token: expectedRevisionToken,
    },
  });

  return response.data;
};

/**
 * Retrieves the Site-level CAPEX log.
 *
 * @param {number|string} farmId - The Site ID.
 * @param {AbortSignal} [signal] - Optional request cancellation signal.
 * @returns {Promise<Object>} The Site's CAPEX log.
 */
export const getCapexLog = async (farmId, signal = undefined) => {
  const response = await axiosInstance.get(`/financial/${farmId}/capex`, { signal });
  return response.data;
};

/** Retrieves one bounded offset page of items for a Financial category. */
export const getFinancialCategoryItems = async (
  categoryId,
  { page = 1, limit = 50, includeSummary = false, signal } = {}
) => {
  const response = await axiosInstance.get(`/financial/categories/${categoryId}/items`, {
    params: { page, limit, ...(includeSummary ? { summary: 1 } : {}) },
    signal,
  });
  return response.data;
};

/**
 * Updates one Financial item.
 *
 * @param {number|string} itemId - The Financial item ID.
 * @param {Object} data - Expected revision plus approved Financial item updates.
 * @returns {Promise<Object>} The updated Financial item.
 */
export const updateFinancialItem = async (itemId, data) => {
  const response = await axiosInstance.put(`/financial/item/${itemId}`, data);
  return response.data;
};

/**
 * Updates multiple Financial items in one request.
 *
 * @param {Array<Object>} items - Financial item updates.
 * @returns {Promise<Array>} The updated Financial items.
 */
export const batchUpdateFinancialItems = async items => {
  const response = await axiosInstance.put("/financial/items/batch", {
    items,
  });

  return response.data;
};

/**
 * Adds a Financial item to an existing category.
 *
 * @param {Object} data - New Financial item data.
 * @returns {Promise<Object>} The created Financial item.
 */
export const addFinancialItem = async data => {
  const response = await axiosInstance.post("/financial/item", data);
  return response.data;
};

/**
 * Deletes a Financial item using the revision loaded by the client.
 *
 * @param {number|string} itemId - The Financial item ID.
 * @param {number} revision - Expected Financial item revision.
 * @returns {Promise<Object>} The deletion result.
 */
export const deleteFinancialItem = async (itemId, revision) => {
  const response = await axiosInstance.delete(`/financial/item/${itemId}`, {
    params: {
      revision,
    },
  });

  return response.data;
};
